import { List, Map } from "immutable";
import {
    CaseRecord,
    CaseDetailRecord,
    CaseTaskRecord,
    CompanyRecord,
    AssociateRecord,
    BankAccountRecord,
    AddressRecord,
    SupplyChainRecord,
    UserRecord,
    StagePermissionRecord,
    StagePermissionsListRecord,
    StatementRecord,
    ScoreCardRecord,
    BusinessProgramRecord,
    BusinessProgramDetailRecord,
    BusinessProgramAttachments,
    AgreementDetailRecord,
    BusinessBasicDetailRecord,
    OnboardingCasesRecord
} from "records";
import moment from "moment";
import { STATE_STATUS } from "constants";
import { FinancialYearRecord, ProgramCompanyRecord, BusinessProductRecord, BuyerQualificationDetailRecord, BusinessProgramCashbackRecord, BusinessProgramFacilityDetailRecord, BusinessProgramFeeRecord, BusinessProgramInterestRecord, BusinessProgramRecourseDetailRecord } from "records/index";

export const pipelineCreator = () => {
    let pipes = [];
    const pipe = (func, params = []) => {
        pipes.push([func, params]);
        return {
            pipe,
            run
        };
    };
    const run = data =>
        pipes.reduce((val, [func, params]) => func(val, ...params), data);

    return {
        pipe,
        run
    };
};

export const CaseRecordPipeline = pipelineCreator()
    .pipe(_ => new CaseRecord(_))
    .pipe(_ =>
        _.withMutations(map =>
            map
                // .set("company", new CompanyRecord(map.company))
                .set("anchor", new CompanyRecord(map.anchor))
                .set("seller", new CompanyRecord(map.seller))
                .set("assignment_date", moment(map.assignment_date))
                .set(
                    "case_task",
                    new CaseTaskRecord(map.case_task).withMutations(map =>
                        map
                            .set("assignment_date", moment(map.assignment_date))
                            .set("create_date", moment(map.create_date))
                            .set("end_date", moment(map.end_date))
                            .set("modified_date", moment(map.modified_date))
                            .set("start_date", moment(map.start_date))
                    )
                )
                .set(
                    "current_case_task",
                    map.current_case_task ? new CaseTaskRecord(map.current_case_task).withMutations(map =>
                        map
                            .set("assignment_date", moment(map.assignment_date))
                            .set("create_date", moment(map.create_date))
                            .set("end_date", moment(map.end_date))
                            .set("modified_date", moment(map.modified_date))
                            .set("start_date", moment(map.start_date))
                    ) : {}
                )
                .set(
                    "current_case_tasks",
                    map.current_case_tasks && map.current_case_tasks.length ? new CaseTaskRecord(map.current_case_tasks.filter(_ => !_.end_date)[0]).withMutations(map => {
                        return map
                            .set("assignment_date", moment(map.assignment_date))
                            .set("create_date", moment(map.create_date))
                            .set("end_date", moment(map.end_date))
                            .set("modified_date", moment(map.modified_date))
                            .set("start_date", moment(map.start_date));
                    }) : []
                )
                .set(
                    "supply_chain",
                    new SupplyChainRecord(map.supply_chain).set(
                        "date_of_appointment",
                        moment(map.supply_chain.date_of_appointment)
                    )
                )
                .set("proposed_limit", +map.proposed_limit)
        )
    );

export const CaseRecordsPipeline = pipelineCreator()
    .pipe(_ => _.map(_ => CaseRecordPipeline.run(_)))
    .pipe(_ => new List(_));

export const AddressRecordPipeline = pipelineCreator()
    .pipe(_ => new AddressRecord(_))
    .pipe(_ => _.set("residing_since", moment(_.residing_since)));

export const BankAccountRecordPipeline = pipelineCreator()
    .pipe(_ => new BankAccountRecord(_))
    .pipe(_ =>
        _.set("associate", _.associate === null ? "company" : _.associate)
    )
    .pipe(_ => _.set("create_date", moment(_.create_date)))
    .pipe(_ => _.set("modified_date", moment(_.modified_date)));

export const AssociateRecordPipeline = pipelineCreator()
    .pipe(_ => new AssociateRecord(_))
    .pipe(_ => _.set("dob", moment(_.dob)))
    .pipe(_ => _.set("dob_pan", moment(_.dob_pan)))
    .pipe(_ => _.set("dob_aadhar", moment(_.dob_aadhar)))
    .pipe(_ => _.set("passport_issue_date", moment(_.passport_issue_date)))
    .pipe(_ => _.set("passport_expiry_date", moment(_.passport_expiry_date)))
    .pipe(_ => _.set("passport_issue_date", moment(_.passport_issue_date)))
    .pipe(_ => _.set("create_date", moment(_.create_date)))
    .pipe(_ => _.set("modified_date", moment(_.modified_date)))
    .pipe(_ => _.set("cibil_supported_report", new List(_.cibil_supported_report)));

export const CompanyRecordPipeline = pipelineCreator()
    .pipe(_ => new CompanyRecord(_))
    .pipe(_ => _.set("date_of_incorporation", moment(_.date_of_incorporation)))
    .pipe(_ => _.set("business_start_date", moment(_.business_start_date)))
    .pipe(_ => _.set("annual_turnover1_date", moment(_.annual_turnover1_date)))
    .pipe(_ => _.set("annual_turnover2_date", moment(_.annual_turnover2_date)))
    .pipe(_ =>
        _.set("credit_rating_expiry_date", moment(_.credit_rating_expiry_date))
    )
    .pipe(_ => _.set("create_date", moment(_.create_date)))
    .pipe(_ => _.set("modified_date", moment(_.modified_date)))
    .pipe(_ =>
        _.set(
            "bank_accounts",
            _.bank_accounts && (_.bank_accounts.size || _.bank_accounts.length) ? new List(
                _.bank_accounts.map(__ => BankAccountRecordPipeline.run(__))
            )
                .toMap()
                .mapEntries(([, v]) => [v.id, v])
                : null
        )
    )
    .pipe(_ =>
        _.set(
            "associates",
            new List(_.associates.map(__ => AssociateRecordPipeline.run(__)))
                .toMap()
                .mapEntries(([, v]) => [v.id, v])
        )
    )
    .pipe(_ =>
        _.set(
            "addresses",
            new List(_.addresses.map(__ => AddressRecordPipeline.run(__)))
                .toMap()
                .mapEntries(([, v]) => [v.id, v])
        )
    );

export const AssociatesPipeline = pipelineCreator()
    .pipe(_ => _.map(__ => AssociateRecordPipeline.run(__)))
    .pipe(_ => new List(_).toMap().mapEntries(([, v]) => [v.id, v]));

export const BankAccountsPipeline = pipelineCreator()
    .pipe(_ => _.map(__ => BankAccountRecordPipeline.run(__)))
    .pipe(_ => new List(_).toMap().mapEntries(([, v]) => [v.id, v]));

export const AddressesPipeline = pipelineCreator()
    .pipe(_ => _.map(__ => AddressRecordPipeline.run(__)))
    .pipe(_ => new List(_).toMap().mapEntries(([, v]) => [v.id, v]));

export const CaseDetailRecordPipeline = pipelineCreator()
    .pipe(_ => new CaseDetailRecord(_))
    // .pipe(_ => _.set("company", CompanyRecordPipeline.run(_.company)))
    .pipe(_ => _.set("anchor", new CompanyRecord(_.anchor)))
    .pipe(_ => _.set("seller", new CompanyRecord(_.seller)))
    .pipe(_ =>
        _.set(
            "financial_year",
            _.financial_year ? new FinancialYearRecord({
                ..._.financial_year,
                start_date: moment(_.financial_year.start_date),
                end_date: moment(_.financial_year.end_date)
            }) : null
        )
    )
    .pipe(_ => _.set("questionnaire", new Map(_.questionnaire).sort((a, b) => {
        if(a.sno < b.sno) {
            return -1;
        }
        if(a.sno > b.sno) {
            return 1;
        }
        if(a.sno === b.sno) {
            return 0;
        }
    })));
// .pipe(_ =>
//     _.set("sales_manager_user", new UserRecord(_.sales_manager_user))
// );

export const UserRecordPipeline = pipelineCreator()
    .pipe(_ => new UserRecord(_))
    .pipe(_ => _.set("groups", new List(_.groups)));

export const StagePermissionRecordPipeline = pipelineCreator()
    .pipe(_ => new StagePermissionRecord(_))
    .pipe(_ => {
        return _.withMutations(__ =>
            __.set("can_proceed", new List(__.can_proceed))
                .set("can_refer", new List(__.can_refer))
                .set("can_edit", _.can_edit ? new List(__.can_edit) : new List([]))
                .set(
                    "is_mandatory_allocation",
                    new List(__.is_mandatory_allocation)
                )
                .set(
                    "columns",
                    new List(__.columns).sort((_, __) =>
                        _[Object.keys(_)[0]] > __[Object.keys(__)[0]] ? 1 : -1
                    )
                )
        );
    });

export const StagePermissionsPipeline = pipelineCreator()
    .pipe(_ => _.map(__ => StagePermissionRecordPipeline.run(__)))
    // .pipe(_ => _.sort((a, b) => (a.id > b.id ? 1 : -1)))
    .pipe(
        _ =>
            new StagePermissionsListRecord({
                data: _,
                status: STATE_STATUS.FETCHED
            })
    );

export const StatementRecordPipeline = pipelineCreator()
    .pipe(_ => new StatementRecord(_))
    .pipe(_ =>
        _.set(
            "txn_month_year",
            _.txn_month_year ? moment(_.txn_month_year) : "0"
        )
    )
    .pipe(_ => _.set("created_by", _.created_by ? moment(_.created_by) : null));

export const StatementPipeline = pipelineCreator()
    .pipe(_ => _.map(__ => StatementRecordPipeline.run(__)))
    .pipe(_ => new List(_).toMap().mapEntries(([, v]) => [v.id, v]));

export const ScoreCardRecordPipeline = pipelineCreator().pipe(_ =>
    new ScoreCardRecord().withMutations(__ => {
        __.set("score", new Map(_.score));
        __.set("groups", new List(_.groups));
        __.set("details", new Map(_.details));
    })
);


export const BusinessProgramPipeline = pipelineCreator()
    .pipe(_ => {
        return new BusinessProgramRecord(_);
    });

export const BusinessProgramDetailPipeline = pipelineCreator()
    .pipe(_ => new BusinessProgramDetailRecord(_))
    .pipe(_ => _.set("company", new ProgramCompanyRecord(_.company)))
    .pipe(_ => _.set("business_product", new BusinessProductRecord(_.business_product)))
    .pipe(_ => _.set("business_program_basic_details", new BusinessBasicDetailRecord(_.business_program_basic_details)))
    .pipe(_ => _.set("business_program_buyer_qualification_detail", new BuyerQualificationDetailRecord(_.business_program_buyer_qualification_detail)))
    .pipe(_ => _.set("business_program_cashback", new BusinessProgramCashbackRecord(_.business_program_cashback)))
    .pipe(_ => _.set(
        "business_program_facility_details",
        new List(_.business_program_facility_details.map(__ =>
            new BusinessProgramFacilityDetailRecord(__)
        ))
            .toMap()
            .mapEntries(([, v]) => [v.id, v])
    ))
    .pipe(_ => _.set(
        "business_program_fee",
        new List(_.business_program_fee.map(__ =>
            new BusinessProgramFeeRecord(__)
        ))
            .toMap()
            .mapEntries(([, v]) => [v.id, v])
    ))
    .pipe(_ => _.set(
        "business_program_interest",
        new List(_.business_program_interest.map(__ =>
            new BusinessProgramInterestRecord(__)
        ))
            .toMap()
            .mapEntries(([, v]) => [v.id, v])
    ))
    .pipe(_ => _.set(
        "business_program_recourse_detail",
        new BusinessProgramRecourseDetailRecord(_.business_program_recourse_detail)
    ))
    .pipe(_ => _.set("business_program_agreement_details", new AgreementDetailRecord({
        id:  _.business_program_agreement_details.id,
        signing_date:  moment(_.business_program_agreement_details.signing_date),
        start_date:  moment(_.business_program_agreement_details.start_date),
        end_date:  moment(_.business_program_agreement_details.end_date)

    })))
    .pipe(_ => _.set("business_program_attachments", new BusinessProgramAttachments(_.business_program_attachments)));



    export const OnboardingCasesListPipeline = pipelineCreator()
    .pipe(_ => _.map(_ => new OnboardingCasesRecord(_)))
